import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { DollarSign } from "lucide-react";
import { getCalculatorSEO } from "@/hooks/useSEO";

export default function TipCalculator() {
  const [billAmount, setBillAmount] = useState("");
  const [tipPercent, setTipPercent] = useState("15");
  const [people, setPeople] = useState("1");
  const [results, setResults] = useState<any>(null);

  const calculateTip = () => {
    const bill = parseFloat(billAmount);
    const tip = parseFloat(tipPercent);
    const numPeople = parseInt(people);
    
    if (!bill || !tip || !numPeople || bill <= 0 || numPeople <= 0) return;
    
    const tipAmount = bill * (tip / 100);
    const total = bill + tipAmount;
    const perPerson = total / numPeople;
    
    setResults({
      tipAmount: tipAmount.toFixed(2),
      total: total.toFixed(2),
      perPerson: perPerson.toFixed(2)
    });
  };

  return (
    <PageLayout calculatorName="Tip Calculator" calculatorPath="/calculator/tip">
      <div className="grid gap-8 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Calculate Tip</CardTitle>
            <CardDescription>Enter bill details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Bill Amount ($)</Label>
              <Input
                type="number"
                value={billAmount}
                onChange={(e) => setBillAmount(e.target.value)}
                placeholder="50.00"
                step="0.01"
              />
            </div>
            <div>
              <Label>Tip Percentage (%)</Label>
              <div className="flex gap-2 mb-2">
                {[10, 15, 18, 20].map((percent) => (
                  <Button
                    key={percent}
                    variant={tipPercent === percent.toString() ? "default" : "outline"}
                    size="sm"
                    onClick={() => setTipPercent(percent.toString())}
                  >
                    {percent}%
                  </Button>
                ))}
              </div>
              <Input
                type="number"
                value={tipPercent}
                onChange={(e) => setTipPercent(e.target.value)}
                placeholder="15"
              />
            </div>
            <div>
              <Label>Number of People</Label>
              <Input
                type="number"
                value={people}
                onChange={(e) => setPeople(e.target.value)}
                placeholder="1"
                min="1"
              />
            </div>
            <Button onClick={calculateTip} className="w-full">
              Calculate
            </Button>
          </CardContent>
        </Card>

        {results && (
          <Card>
            <CardHeader>
              <CardTitle>Results</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between text-lg">
                <span>Tip Amount:</span>
                <span className="font-semibold">${results.tipAmount}</span>
              </div>
              <div className="flex justify-between text-lg">
                <span>Total:</span>
                <span className="font-semibold">${results.total}</span>
              </div>
              {parseInt(people) > 1 && (
                <div className="flex justify-between text-lg border-t pt-4">
                  <span>Per Person:</span>
                  <span className="font-semibold text-primary">${results.perPerson}</span>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </PageLayout>
  );
}
